// ---------------------------------------------------------------------------
// Rohrkonfigurator data — AustroPUR PEX heating pipes.
//
// Selection works off two row-aligned lookup tables:
//   • DT_KW[row][ΔT-column]   → heat power (kW) a given mass-flow row carries at
//     each temperature spread ΔT.
//   • KW_PIPE[row][pipe-column] → specific pressure drop (Pa/m) for each pipe
//     diameter at that same mass-flow row; null = the pipe is unsuitable there.
//
// Given a required power and ΔT: snap ΔT to the nearest column, find the row whose
// kW in that column is closest to the requirement, then read that row of KW_PIPE
// for the suitable diameters and their pressure drops.
//
// TEMP_LOSS_* give insulation U-values (W/m·K) per pipe dimension for the heat-loss
// estimate. Values supplied from the AustroPUR datasheets.
// ---------------------------------------------------------------------------

/** Temperature spreads ΔT (K) — the columns of DT_KW. */
export const DELTA_T_COLUMNS = [5, 7, 10, 15, 20, 25, 30, 40];

/** Pipe outer diameters (mm) — the columns of KW_PIPE. */
export const PIPE_COLUMNS = [20, 25, 32, 40, 50, 63, 75, 90, 110, 125, 140, 160];

/** Heat power (kW) per mass-flow row, one value per ΔT column. */
export const DT_KW: number[][] = [
  [0.25, 0.35, 0.5, 0.75, 1, 1.25, 1.5, 2],
  [0.625, 0.875, 1.25, 1.875, 2.5, 3.125, 3.75, 5],
  [1.25, 1.75, 2.5, 3.75, 5, 6.25, 7.5, 10],
  [2.5, 3.5, 5, 7.5, 10, 12.5, 15, 20],
  [3.75, 5.25, 7.5, 11.25, 15, 18.75, 22.5, 30],
  [5, 7, 10, 15, 20, 25, 30, 40],
  [6.25, 8.75, 12.5, 18.75, 25, 31.25, 37.5, 50],
  [7.5, 10.5, 15, 22.5, 30, 37.5, 45, 60],
  [8.75, 12.25, 17.5, 26.25, 35, 43.75, 52.5, 70],
  [10, 14, 20, 30, 40, 50, 60, 80],
  [11.25, 15.75, 22.5, 33.75, 45, 56.25, 67.5, 90],
  [12.5, 17.5, 25, 37.5, 50, 62.5, 75, 100],
  [13.75, 19.25, 27.5, 41.25, 55, 68.75, 82.5, 110],
  [15, 21, 30, 45, 60, 75, 90, 120],
  [16.25, 22.75, 32.5, 48.75, 65, 81.25, 97.5, 130],
  [17.5, 24.5, 35, 52.5, 70, 87.5, 105, 140],
  [18.75, 26.25, 37.5, 56.25, 75, 93.75, 112.5, 150],
  [20, 28, 40, 60, 80, 100, 120, 160],
  [21.25, 29.75, 42.5, 63.75, 85, 106.25, 127.5, 170],
  [22.5, 31.5, 45, 67.5, 90, 112.5, 135, 180],
  [25, 35, 50, 75, 100, 125, 150, 200],
  [27.5, 38.5, 55, 82.5, 110, 137.5, 165, 220],
  [30, 42, 60, 90, 120, 150, 180, 240],
  [32.5, 45.5, 65, 97.5, 130, 162.5, 195, 260],
  [35, 49, 70, 105, 140, 175, 210, 280],
  [37.5, 52.5, 75, 112.5, 150, 187.5, 225, 300],
  [40, 56, 80, 120, 160, 200, 240, 320],
  [42.5, 59.5, 85, 127.5, 170, 212.5, 255, 340],
  [45, 63, 90, 135, 180, 225, 270, 360],
  [50, 70, 100, 150, 200, 250, 300, 400],
  [56.25, 78.75, 112.5, 168.75, 225, 281.25, 337.5, 450],
  [62.5, 87.5, 125, 187.5, 250, 312.5, 375, 500],
  [68.75, 96.25, 137.5, 206.25, 275, 343.75, 412.5, 550],
  [75, 105, 150, 225, 300, 375, 450, 600],
  [81.25, 113.75, 162.5, 243.75, 325, 406.25, 487.5, 650],
  [87.5, 122.5, 175, 262.5, 350, 437.5, 525, 700],
  [93.75, 131.25, 187.5, 281.25, 375, 468.75, 562.5, 750],
  [100, 140, 200, 300, 400, 500, 600, 800],
  [106.25, 148.75, 212.5, 318.75, 425, 531.25, 637.5, 850],
  [112.5, 157.5, 225, 337.5, 450, 562.5, 675, 900],
  [118.75, 166.25, 237.5, 356.25, 475, 593.75, 712.5, 950],
  [125, 175, 250, 375, 500, 625, 750, 1000],
  [131.25, 183.75, 262.5, 393.75, 525, 656.25, 787.5, 1050],
  [137.5, 192.5, 275, 412.5, 550, 687.5, 825, 1100],
  [143.75, 201.25, 287.5, 431.25, 575, 718.75, 862.5, 1150],
  [150, 210, 300, 450, 600, 750, 900, 1200],
  [156.25, 218.75, 312.5, 468.75, 625, 781.25, 937.5, 1250],
  [162.5, 227.5, 325, 487.5, 650, 812.5, 975, 1300],
  [168.75, 236.25, 337.5, 506.25, 675, 843.75, 1012.5, 1350],
  [175, 245, 350, 525, 700, 875, 1050, 1400],
  [181.25, 253.75, 362.5, 543.75, 725, 906.25, 1087.5, 1450],
  [187.5, 262.5, 375, 562.5, 750, 937.5, 1125, 1500],
  [193.75, 271.25, 387.5, 581.25, 775, 968.75, 1162.5, 1550],
  [200, 280, 400, 600, 800, 1000, 1200, 1600],
  [212.5, 297.5, 425, 637.5, 850, 1062.5, 1275, 1700],
  [225, 315, 450, 675, 900, 1125, 1350, 1800],
  [237.5, 332.5, 475, 712.5, 950, 1187.5, 1425, 1900],
  [250, 350, 500, 750, 1000, 1250, 1500, 2000],
  [262.5, 367.5, 525, 787.5, 1050, 1312.5, 1575, 2100],
  [275, 385, 550, 825, 1100, 1375, 1650, 2200],
  [287.5, 402.5, 575, 862.5, 1150, 1437.5, 1725, 2300],
  [300, 420, 600, 900, 1200, 1500, 1800, 2400],
  [312.5, 437.5, 625, 937.5, 1250, 1562.5, 1875, 2500],
  [325, 455, 650, 975, 1300, 1625, 1950, 2600],
  [337.5, 472.5, 675, 1012.5, 1350, 1687.5, 2025, 2700],
  [350, 490, 700, 1050, 1400, 1750, 2100, 2800],
  [362.5, 507.5, 725, 1087.5, 1450, 1812.5, 2175, 2900],
  [375, 525, 750, 1125, 1500, 1875, 2250, 3000],
  [387.5, 542.5, 775, 1162.5, 1550, 1937.5, 2325, 3100],
  [400, 560, 800, 1200, 1600, 2000, 2400, 3200],
  [412.5, 577.5, 825, 1237.5, 1650, 2062.5, 2475, 3300],
  [425, 595, 850, 1275, 1700, 2125, 2550, 3400],
  [437.5, 612.5, 875, 1312.5, 1750, 2187.5, 2625, 3500],
  [450, 630, 900, 1350, 1800, 2250, 2700, 3600],
  [462.5, 647.5, 925, 1387.5, 1850, 2312.5, 2775, 3700],
  [475, 665, 950, 1425, 1900, 2375, 2850, 3800],
  [487.5, 682.5, 975, 1462.5, 1950, 2437.5, 2925, 3900],
  [500, 700, 1000, 1500, 2000, 2500, 3000, 4000],
  [525, 735, 1050, 1575, 2100, 2625, 3150, 4200],
  [550, 770, 1100, 1650, 2200, 2750, 3300, 4400],
  [575, 805, 1150, 1725, 2300, 2875, 3450, 4600],
  [600, 840, 1200, 1800, 2400, 3000, 3600, 4800],
  [625, 875, 1250, 1875, 2500, 3125, 3750, 5000],
];

/** Specific pressure drop (Pa/m) per pipe diameter; null = unsuitable at this row. */
export const KW_PIPE: (number | null)[][] = [
  [5, null, null, null, null, null, null, null, null, null, null, null],
  [24, null, null, null, null, null, null, null, null, null, null, null],
  [80, 27, null, null, null, null, null, null, null, null, null, null],
  [273, 90, null, null, null, null, null, null, null, null, null, null],
  [565, 185, null, null, null, null, null, null, null, null, null, null],
  [null, 310, null, null, null, null, null, null, null, null, null, null],
  [null, 465, 138, null, null, null, null, null, null, null, null, null],
  [null, null, 192, null, null, null, null, null, null, null, null, null],
  [null, null, 254, null, null, null, null, null, null, null, null, null],
  [null, null, 323, 112, null, null, null, null, null, null, null, null],
  [null, null, 400, 139, null, null, null, null, null, null, null, null],
  [null, null, null, 168, null, null, null, null, null, null, null, null],
  [null, null, null, 199, null, null, null, null, null, null, null, null],
  [null, null, null, 233, null, null, null, null, null, null, null, null],
  [null, null, null, 270, null, null, null, null, null, null, null, null],
  [null, null, null, 309, 104, null, null, null, null, null, null, null],
  [null, null, null, 350, 118, null, null, null, null, null, null, null],
  [null, null, null, 394, 132, null, null, null, null, null, null, null],
  [null, null, null, null, 148, null, null, null, null, null, null, null],
  [null, null, null, null, 164, 54, null, null, null, null, null, null],
  [null, null, null, null, 199, 65, null, null, null, null, null, null],
  [null, null, null, null, 237, 77, null, null, null, null, null, null],
  [null, null, null, null, 277, 90, null, null, null, null, null, null],
  [null, null, null, null, 321, 104, null, null, null, null, null, null],
  [null, null, null, null, 368, 119, null, null, null, null, null, null],
  [null, null, null, null, null, 135, null, null, null, null, null, null],
  [null, null, null, null, null, 152, null, null, null, null, null, null],
  [null, null, null, null, null, 170, null, null, null, null, null, null],
  [null, null, null, null, null, 189, 80, null, null, null, null, null],
  [null, null, null, null, null, 229, 96, null, null, null, null, null],
  [null, null, null, null, null, 285, 120, null, null, null, null, null],
  [null, null, null, null, null, 346, 145, null, null, null, null, null],
  [null, null, null, null, null, 412, 173, 71, null, null, null, null],
  [null, null, null, null, null, null, 203, 84, null, null, null, null],
  [null, null, null, null, null, null, 235, 97, null, null, null, null],
  [null, null, null, null, null, null, 269, 111, null, null, null, null],
  [null, null, null, null, null, null, 306, 126, 47, null, null, null],
  [null, null, null, null, null, null, 345, 142, 53, null, null, null],
  [null, null, null, null, null, null, 386, 159, 60, null, null, null],
  [null, null, null, null, null, null, null, 176, 66, null, null, null],
  [null, null, null, null, null, null, null, 195, 73, null, null, null],
  [null, null, null, null, null, null, null, 214, 80, null, null, null],
  [null, null, null, null, null, null, null, 234, 88, 47, null, null],
  [null, null, null, null, null, null, null, 256, 96, 51, null, null],
  [null, null, null, null, null, null, null, 278, 104, 56, 32, null],
  [null, null, null, null, null, null, null, 300, 112, 60, 35, null],
  [null, null, null, null, null, null, null, 324, 121, 65, 37, null],
  [null, null, null, null, null, null, null, 349, 130, 70, 40, 21],
  [null, null, null, null, null, null, null, 374, 139, 75, 43, 23],
  [null, null, null, null, null, null, null, 400, 149, 80, 46, 24],
  [null, null, null, null, null, null, null, null, 159, 85, 49, 26],
  [null, null, null, null, null, null, null, null, 169, 91, 52, 27],
  [null, null, null, null, null, null, null, null, 180, 97, 55, 29],
  [null, null, null, null, null, null, null, null, 191, 102, 59, 31],
  [null, null, null, null, null, null, null, null, 214, 115, 65, 34],
  [null, null, null, null, null, null, null, null, 237, 127, 73, 38],
  [null, null, null, null, null, null, null, null, 263, 141, 80, 42],
  [null, null, null, null, null, null, null, null, 289, 155, 88, 46],
  [null, null, null, null, null, null, null, null, 317, 169, 97, 51],
  [null, null, null, null, null, null, null, null, 345, 185, 105, 55],
  [null, null, null, null, null, null, null, null, 375, 201, 114, 60],
  [null, null, null, null, null, null, null, null, 406, 217, 124, 65],
  [null, null, null, null, null, null, null, null, null, 234, 134, 70],
  [null, null, null, null, null, null, null, null, null, 252, 144, 75],
  [null, null, null, null, null, null, null, null, null, 270, 154, 81],
  [null, null, null, null, null, null, null, null, null, 290, 165, 86],
  [null, null, null, null, null, null, null, null, null, null, 176, 92],
  [null, null, null, null, null, null, null, null, null, null, 187, 98],
  [null, null, null, null, null, null, null, null, null, null, 199, 104],
  [null, null, null, null, null, null, null, null, null, null, 211, 110],
  [null, null, null, null, null, null, null, null, null, null, 224, 117],
  [null, null, null, null, null, null, null, null, null, null, 237, 124],
  [null, null, null, null, null, null, null, null, null, null, null, 130],
  [null, null, null, null, null, null, null, null, null, null, null, 137],
  [null, null, null, null, null, null, null, null, null, null, null, 145],
  [null, null, null, null, null, null, null, null, null, null, null, 152],
  [null, null, null, null, null, null, null, null, null, null, null, 159],
  [null, null, null, null, null, null, null, null, null, null, null, 167],
  [null, null, null, null, null, null, null, null, null, null, null, 183],
  [null, null, null, null, null, null, null, null, null, null, null, 200],
  [null, null, null, null, null, null, null, null, null, null, null, 217],
  [null, null, null, null, null, null, null, null, null, null, null, 235],
  [null, null, null, null, null, null, null, null, null, null, null, 254],
];

export type TempLoss = { insulation: string; dimension: string; uWert: number };

/** Heat-loss U-values (W/m·K) for double pipes (Doppelrohr). */
export const TEMP_LOSS_DUO: TempLoss[] = [
  { insulation: "A125", dimension: "20", uWert: 0.1286 },
  { insulation: "A125", dimension: "25", uWert: 0.1577 },
  { insulation: "A145", dimension: "25", uWert: 0.132 },
  { insulation: "A145", dimension: "32", uWert: 0.1681 },
  { insulation: "A175", dimension: "32", uWert: 0.1372 },
  { insulation: "A175", dimension: "45", uWert: 0.1726 },
  { insulation: "A200", dimension: "40", uWert: 0.1444 },
  { insulation: "A200", dimension: "50", uWert: 0.1851 },
  { insulation: "A200", dimension: "63", uWert: 0.2517 },
  { insulation: "A240", dimension: "50", uWert: 0.1477 },
  { insulation: "A240", dimension: "63", uWert: 0.1873 },
  { insulation: "A240", dimension: "75", uWert: 0.2527 },
];

/** Heat-loss U-values (W/m·K) for single pipes (Einzelrohr). */
export const TEMP_LOSS_SINGLE: TempLoss[] = [
  { insulation: "A125", dimension: "25", uWert: 0.0899 },
  { insulation: "A125", dimension: "32", uWert: 0.1072 },
  { insulation: "A145", dimension: "40", uWert: 0.112 },
  { insulation: "A145", dimension: "50", uWert: 0.1368 },
  { insulation: "A175", dimension: "63", uWert: 0.1436 },
  { insulation: "A200", dimension: "63", uWert: 0.1236 },
  { insulation: "A175", dimension: "75", uWert: 0.1756 },
  { insulation: "A200", dimension: "75", uWert: 0.1466 },
  { insulation: "A200", dimension: "90", uWert: 0.182 },
  { insulation: "A240", dimension: "90", uWert: 0.1457 },
  { insulation: "A200", dimension: "110", uWert: 0.2477 },
  { insulation: "A240", dimension: "110", uWert: 0.1853 },
  { insulation: "A240", dimension: "125", uWert: 0.2237 },
  { insulation: "A250", dimension: "160", uWert: 0.2816 },
];
